/**
 * Salepify & BSS Gift Filter Script
 * Store: Crepes Lifestyle
 *
 * Requirements:
 * 1. Default display: "Free Necklace" (ID: 24780) and "Free bracelet" (ID: 24753)
 * 2. 1 BOGO offer only: dynamically picked based on the highest-priced non-gift item in cart
 * 3. Offer count in header updated to 3
 * 4. Auto-reload on /cart page when a gift is added so it immediately appears in the cart table
 * 5. Lock gift quantity in cart table (prevent customer from changing quantity)
 * 6. BSS Gift Guard (prevent checkout if gift total > paid total)
 */

(function () {
  "use strict";

  // =========================================================================
  // CONSTANTS & CONFIGURATION
  // =========================================================================
  const DEFAULT_OFFER_IDS = ["24780", "24753"]; // Free Necklace & Free bracelet

  const BOGO_RULES = [
    { id: "25580", threshold: 900, name: "BOGO - UNDER 900" },
    { id: "25581", threshold: 950, name: "BOGO - UNDER 950" },
    { id: "25582", threshold: 1000, name: "BOGO - UNDER 1K" },
    { id: "25578", threshold: 1500, name: "BOGO - UNDER 1.5K" },
    { id: "25583", threshold: 2000, name: "BOGO - UNDER 2K" },
    { id: "25584", threshold: 2500, name: "BOGO - UNDER 2.5K" },
    { id: "25585", threshold: 3000, name: "BOGO - UNDER 3K" },
    { id: "25586", threshold: 3500, name: "BOGO - UNDER 3.5K" },
    { id: "25587", threshold: 4000, name: "BOGO - UNDER 4K" },
    { id: "25588", threshold: 4500, name: "BOGO - UNDER 4.5K" },
    { id: "25589", threshold: 6000, name: "BOGO - UNDER 6K" }
  ];

  const ALL_BOGO_IDS = BOGO_RULES.map(r => r.id);

  let currentActiveBogoId = "25578"; // Default fallback (UNDER 1.5K)
  let lastCartSignature = "";

  // =========================================================================
  // HELPER FUNCTIONS
  // =========================================================================
  function isGiftLineItem(item) {
    if (!item) return false;
    if (item.price === 0 || item.final_price === 0) return true;
    if (item.properties) {
      for (const k of Object.keys(item.properties)) {
        if (k.toLowerCase().includes("salepify") || k.toLowerCase().includes("freegift") || k.charAt(0) === "_") {
          return true;
        }
      }
    }
    return false;
  }

  function getClaimedBogoIdFromCart(cart) {
    if (!cart || !cart.items) return null;
    for (const item of cart.items) {
      if (item.properties) {
        for (const [propKey, propVal] of Object.entries(item.properties)) {
          if (propKey.includes("salepify") || propKey.includes("bogo")) {
            const valStr = String(propVal).trim();
            if (ALL_BOGO_IDS.includes(valStr)) return valStr;
          }
        }
      }
    }
    return null;
  }

  function calculateActiveBogoId(cart) {
    // 1. If customer already claimed a BOGO gift, lock to that tier
    const claimedId = getClaimedBogoIdFromCart(cart);
    if (claimedId) return claimedId;

    // 2. Find highest priced non-gift product in cart
    let maxPrice = 0;
    if (cart && cart.items && cart.items.length > 0) {
      for (const item of cart.items) {
        if (!isGiftLineItem(item)) {
          const unitPrice = (item.price || item.original_price || 0) / 100;
          if (unitPrice > maxPrice) {
            maxPrice = unitPrice;
          }
        }
      }
    }

    if (maxPrice <= 0 && cart && cart.total_price) {
      maxPrice = cart.total_price / 100;
    }

    if (maxPrice <= 0) {
      return BOGO_RULES[0].id;
    }

    // 3. Find matching BOGO tier (<= threshold)
    for (const rule of BOGO_RULES) {
      if (maxPrice <= rule.threshold) {
        return rule.id;
      }
    }
    return BOGO_RULES[BOGO_RULES.length - 1].id;
  }

  // =========================================================================
  // 1. GIFT IN CART FILTER & HEADER COUNT
  // =========================================================================
  function updateFilterCSS(activeBogoId) {
    let style = document.getElementById("bss-bogo-target-css");
    if (!style) {
      style = document.createElement("style");
      style.id = "bss-bogo-target-css";
      (document.head || document.documentElement).appendChild(style);
    }

    // Only hide the 10 inactive BOGO cards. NEVER touch default offers (24780, 24753) or active BOGO.
    const inactiveIds = ALL_BOGO_IDS.filter(id => id !== activeBogoId);
    const selectors = inactiveIds.map(
      id => `.salepify-gic__offer-card[data-rule-key^="${id}-"], .salepify-gic__offer-card[data-rule-key="${id}"]`
    );

    style.textContent = `
      ${selectors.join(",\n")} {
        display: none !important;
      }
      .salepify-gic__offer-scroll {
        overflow-x: hidden;
      }
    `;
  }

  function updateHeaderCount(count = 3) {
    const offerNumEls = document.querySelectorAll(".salepify-gic__offer-number");
    offerNumEls.forEach(el => {
      const current = (el.textContent || "").trim();
      if (/\d+/.test(current)) {
        el.textContent = current.replace(/\d+/, String(count));
      } else {
        el.textContent = count + " offer(s)";
      }
    });
  }

  function updateScrollButtons() {
    document.querySelectorAll(".salepify-gic__gic-container").forEach(container => {
      const list = container.querySelector(".salepify-gic__offer-list");
      const leftBtn = container.querySelector(".salepify-gic__chevron-button--left");
      const rightBtn = container.querySelector(".salepify-gic__chevron-button--right");
      if (!list) return;

      const maxScroll = list.scrollWidth - list.clientWidth;
      const hasOverflow = maxScroll > 10;
      if (leftBtn) leftBtn.style.display = hasOverflow ? "" : "none";
      if (rightBtn) rightBtn.style.display = hasOverflow ? "" : "none";
    });
  }

  function applyOfferFilter() {
    const inactiveIds = ALL_BOGO_IDS.filter(id => id !== currentActiveBogoId);

    const cards = document.querySelectorAll(".salepify-gic__offer-card");
    cards.forEach(card => {
      const key = card.getAttribute("data-rule-key") || "";
      const id = key.split("-")[0];
      if (inactiveIds.includes(id)) {
        card.style.setProperty("display", "none", "important");
        card.setAttribute("data-bogo-hidden", "true");
      } else {
        card.style.removeProperty("display");
        card.removeAttribute("data-bogo-hidden");
      }
    });

    updateFilterCSS(currentActiveBogoId);
    updateHeaderCount(3);
    updateScrollButtons();
  }

  function refreshCartData(force = false) {
    return fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(r => r.json())
      .then(cart => {
        const sig = [
          cart.original_total_price,
          cart.item_count,
          (cart.items || []).map(i => i.variant_id + ":" + i.quantity).join(",")
        ].join("|");

        if (!force && sig === lastCartSignature) {
          applyOfferFilter();
          return;
        }

        lastCartSignature = sig;
        currentActiveBogoId = calculateActiveBogoId(cart);
        applyOfferFilter();
      })
      .catch(err => {
        console.warn("[Gift Filter] /cart.js fetch error:", err);
        applyOfferFilter();
      });
  }

  // =========================================================================
  // 2. AUTO-RELOAD ON /cart PAGE WHEN GIFT IS CLAIMED
  // =========================================================================
  function setupAutoReloadOnCart() {
    const isCartPage = window.location.pathname.endsWith("/cart") || window.location.pathname === "/cart";
    if (!isCartPage) return;

    let reloading = false;
    function triggerCartReload(delay = 800) {
      if (reloading) return;
      reloading = true;
      console.log("[Gift Filter] Gift added! Reloading cart page in " + delay + "ms...");
      setTimeout(() => {
        window.location.reload();
      }, delay);
    }

    // Intercept fetch /cart/add
    if (window.fetch && !window.fetch.__gicAutoReload) {
      const origFetch = window.fetch;
      window.fetch = function (url, options) {
        const urlStr = typeof url === "string" ? url : (url && url.url) || "";
        const p = origFetch.apply(this, arguments);
        if (/\/cart\/add(\.js)?/i.test(urlStr)) {
          p.then(res => {
            if (res.ok) triggerCartReload(800);
          }).catch(() => {});
        }
        return p;
      };
      window.fetch.__gicAutoReload = true;
    }

    // Intercept XHR /cart/add
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    if (!origOpen.__gicAutoReload) {
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__gicUrl = url;
        return origOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        if (/\/cart\/add(\.js)?/i.test(this.__gicUrl || "")) {
          this.addEventListener("load", () => {
            if (this.status >= 200 && this.status < 300) {
              triggerCartReload(800);
            }
          });
        }
        return origSend.apply(this, arguments);
      };
      origOpen.__gicAutoReload = true;
    }

    // Click on Claim / Add gift button
    document.addEventListener("click", e => {
      const btn = e.target.closest?.(
        ".salepify-gic__gift-selector-add-to-cart-button, .salepify-gic__progress, [onclick*='handleClaimGifts']"
      );
      if (btn) {
        const obs = new MutationObserver(() => {
          if (
            btn.classList.contains("salepify-gic__gift-selector-add-to-cart-button--success") ||
            btn.innerHTML.includes("polyline")
          ) {
            obs.disconnect();
            triggerCartReload(800);
          }
        });
        obs.observe(btn, { attributes: true, childList: true, subtree: true });
        setTimeout(() => obs.disconnect(), 6000);
      }
    }, true);
  }

  // =========================================================================
  // 3. LOCK GIFT QUANTITY IN CART
  // =========================================================================
  let isLocking = false;
  let lockTimer = null;

  function lockGiftQuantityInCart() {
    if (isLocking) return;
    isLocking = true;

    fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(res => res.json())
      .then(cart => {
        try {
          const gifts = (cart.items || []).filter(item =>
            (item.properties && Object.keys(item.properties).some(k => k.toLowerCase().includes("salepify"))) ||
            item.price === 0 || item.final_price === 0
          );

          if (gifts.length === 0) {
            isLocking = false;
            return;
          }

          gifts.forEach(gift => {
            try {
              const key = String(gift.key);
              const removeLink = document.querySelector(`a[href*="${key}"], a[href*="${encodeURIComponent(key)}"]`);
              const row = removeLink
                ? removeLink.closest("tr.cart-item, .cart-item")
                : [...document.querySelectorAll("tr.cart-item, .cart-item")].find(r =>
                    r.querySelector("[data-line-item-key]")?.dataset.lineItemKey === key
                  );

              if (!row) return;

              const qtyCell = row.querySelector(".cart-item__quantity, [class*='quantity']");
              if (!qtyCell) return;

              qtyCell.style.pointerEvents = "none";
              qtyCell.style.opacity = "0.7";
              qtyCell.querySelectorAll('button, [class*="button"]').forEach(btn => {
                btn.style.setProperty("display", "none", "important");
              });

              const input = qtyCell.querySelector("input");
              if (input) {
                input.disabled = true;
                input.style.border = "none";
                input.style.textAlign = "center";
              }
            } catch (err) {}
          });
        } catch (e) {
        } finally {
          isLocking = false;
        }
      })
      .catch(() => {
        isLocking = false;
      });
  }

  function debouncedLock() {
    clearTimeout(lockTimer);
    lockTimer = setTimeout(lockGiftQuantityInCart, 300);
  }

  // =========================================================================
  // 4. BSS GIFT GUARD (CHECKOUT VALIDATION)
  // =========================================================================
  const CHECKOUT_SELECTORS = [
    "#checkout",
    '[name="checkout"]',
    ".cart__checkout-button",
    "[data-cart-checkout]",
    'button[type="submit"][name="checkout"]',
    'input[type="submit"][name="checkout"]'
  ];

  function formatMoney(cents) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "INR";
      const amount = cents / 100;
      return typeof Intl !== "undefined" && Intl.NumberFormat
        ? new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(amount)
        : "Rs. " + amount.toFixed(2);
    } catch {
      return "Rs. " + (cents / 100).toFixed(2);
    }
  }

  function showGuardWarning(giftTotal, paidTotal) {
    try {
      const existing = document.getElementById("bss-gift-guard-warning");
      if (existing) existing.remove();

      const msg = document.createElement("div");
      msg.id = "bss-gift-guard-warning";
      msg.setAttribute("role", "alert");
      msg.style.cssText = [
        "position:fixed",
        "top:16px",
        "left:50%",
        "transform:translateX(-50%)",
        "z-index:2147483647",
        "background:#b91c1c",
        "color:#fff",
        "padding:14px 24px",
        "border-radius:8px",
        "font-size:14px",
        "font-family:inherit",
        "box-shadow:0 4px 24px rgba(0,0,0,0.25)",
        "max-width:90vw",
        "text-align:center",
        "line-height:1.5",
        "cursor:pointer"
      ].join(";");
      msg.innerHTML =
        "&#9888;&#65039; Cannot proceed to checkout<br>The free gift value (<strong>" +
        formatMoney(giftTotal) +
        "</strong>) cannot exceed the order value (<strong>" +
        formatMoney(paidTotal) +
        "</strong>).<br><small>Please adjust your cart and try again.</small>";
      msg.addEventListener("click", () => msg.remove());
      document.body.appendChild(msg);
      setTimeout(() => { msg.remove(); }, 5000);
    } catch {
      alert("The free gift value cannot exceed your order value. Please adjust your cart and try again.");
    }
  }

  function attachGuardToButton(btn) {
    if (btn.getAttribute("data-bss-gift-guard") === "true") return;
    btn.setAttribute("data-bss-gift-guard", "true");

    btn.addEventListener("click", function guardHandler(e) {
      try {
        e.preventDefault();
        e.stopImmediatePropagation();
        const origDisabled = btn.disabled;
        btn.disabled = true;

        fetch("/cart.js", { credentials: "same-origin", headers: { "Content-Type": "application/json" } })
          .then(r => r.json())
          .then(cart => {
            let paidTotal = 0;
            let giftTotal = 0;
            (cart.items || []).forEach(item => {
              if (isGiftLineItem(item)) {
                const linePrice = item.original_line_price ?? (item.original_price ?? item.price ?? 0) * (item.quantity || 1);
                giftTotal += linePrice;
              } else {
                paidTotal += (item.final_line_price ?? item.line_price ?? 0);
              }
            });

            if (giftTotal <= paidTotal) {
              btn.removeEventListener("click", guardHandler, true);
              btn.removeAttribute("data-bss-gift-guard");
              btn.disabled = origDisabled;
              btn.click();
              setTimeout(() => attachGuardToButton(btn), 0);
            } else {
              btn.disabled = origDisabled;
              showGuardWarning(giftTotal, paidTotal);
            }
          })
          .catch(() => {
            btn.removeEventListener("click", guardHandler, true);
            btn.removeAttribute("data-bss-gift-guard");
            btn.disabled = origDisabled;
            btn.click();
          });
      } catch {
        btn.removeAttribute("data-bss-gift-guard");
        btn.click();
      }
    }, true);
  }

  function attachGuardToAllButtons() {
    document.querySelectorAll(CHECKOUT_SELECTORS.join(",")).forEach(attachGuardToButton);
  }

  // =========================================================================
  // INITIALIZATION
  // =========================================================================
  function initAll() {
    updateFilterCSS(currentActiveBogoId);
    setupAutoReloadOnCart();
    refreshCartData(true);
    lockGiftQuantityInCart();
    attachGuardToAllButtons();

    // DOM Observer for dynamically rendered cards & cart updates
    let rafId = null;
    const observer = new MutationObserver(mutations => {
      let shouldReapply = false;
      let hasCartChange = false;

      for (const m of mutations) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1) {
            if (
              n.matches?.(".salepify-gic__gic-container, .salepify-gic__offer-card, .salepify-gic__offer-number") ||
              n.querySelector?.(".salepify-gic__gic-container, .salepify-gic__offer-card, .salepify-gic__offer-number")
            ) {
              shouldReapply = true;
            }
            if (n.matches?.("tr.cart-item, .cart-item") || n.querySelector?.("tr.cart-item, .cart-item")) {
              hasCartChange = true;
            }
            if (n.matches?.(CHECKOUT_SELECTORS.join(",")) || n.querySelector?.(CHECKOUT_SELECTORS.join(","))) {
              attachGuardToAllButtons();
            }
          }
        }
      }

      if (shouldReapply) {
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            rafId = null;
            applyOfferFilter();
          });
        }
      }

      if (hasCartChange) {
        debouncedLock();
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    setInterval(() => refreshCartData(false), 2000);
    window.addEventListener("pageshow", () => refreshCartData(true));
    window.addEventListener("load", lockGiftQuantityInCart);
    ["TLCARTGOAL:add", "TLCARTGOAL:change", "TLCARTGOAL:update"].forEach(evt => {
      window.addEventListener(evt, () => setTimeout(lockGiftQuantityInCart, 300));
    });
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", initAll)
    : initAll();

  window.BSSGiftFilter = {
    refresh: () => refreshCartData(true),
    getActiveBogoId: () => currentActiveBogoId,
    apply: applyOfferFilter
  };
})();
